import { PiCheck, PiSpinner, PiWarningCircle, PiInfo } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { FileUploadStatusProps } from '../../../types/whatsapp/file-preview.types';

/** Exposes upload progress and errors without obscuring the selected file. */
export const FileUploadStatus = ({
  uploadStep,
  uploadError,
  fileSize,
}: FileUploadStatusProps) => {
  const { t } = useI18n();
  if (uploadStep === 'error') {
    return (
      <div role="status" className="flex min-w-0">
        <Tooltip
          interactive
          as="span"
          size="sm"
          className="max-w-[min(20rem,calc(100vw-2rem))] break-words"
          content={
            uploadError ?? t('communications.attachment.errorExplanation')
          }
        >
          <Button
            appearance="ghost"
            variant="danger"
            size="2xs"
            className="!h-auto !min-h-0 !justify-start !gap-1 !p-0"
          >
            <PiWarningCircle className="h-3.5 w-3.5 shrink-0" />
            <Text as="span" size="tiny">
              {t('communications.attachment.failed')}
            </Text>
            <PiInfo className="h-3.5 w-3.5 shrink-0" />
          </Button>
        </Tooltip>
      </div>
    );
  }
  const uploading = uploadStep === 'uploading' || uploadStep === 'validating';
  const label = uploading
    ? t('communications.attachment.uploading')
    : uploadStep === 'success'
      ? t('communications.attachment.ready')
      : fileSize;
  return (
    <div
      role="status"
      className="flex min-w-0 items-center gap-1 text-neutral-500"
    >
      {uploading ? (
        <PiSpinner className="h-3.5 w-3.5 shrink-0 animate-spin" />
      ) : uploadStep === 'success' ? (
        <PiCheck className="h-3.5 w-3.5 shrink-0" />
      ) : null}
      <Text as="span" size="tiny" className="min-w-0 truncate">
        {label}
      </Text>
      {uploadStep === 'success' && (
        <Text as="span" size="tiny" className="shrink-0">
          · {fileSize}
        </Text>
      )}
    </div>
  );
};
